import React, { Suspense, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/config/i18n';
import { DOCS_SLUG_TO_FILE } from '@/data/docsArticles';
import DocsArticleLayout from './DocsArticleLayout';

const LANG_PREFIXES = ['pl', 'de', 'es', 'fil', 'hi', 'id', 'pt-br', 'pt-pt', 'tr', 'ar', 'vi', 'th', 'ja', 'ko', 'fr', 'it', 'uk', 'cs', 'nl'];

/** Per-language cloned i18n instances. cloneInstance shares resource bundles
 *  with the parent but has its own independent `language` field, so concurrent
 *  SSG renders can't clobber each other. Cached so React doesn't churn. */
const i18nByLang = new Map<string, ReturnType<typeof i18n.cloneInstance>>();
function getI18nForLang(lang: string) {
  let inst = i18nByLang.get(lang);
  if (!inst) {
    inst = i18n.cloneInstance({ lng: lang });
    i18nByLang.set(lang, inst);
  }
  return inst;
}

const docsI18nModules = import.meta.glob<{ default: React.ComponentType }>(
  '../pages/docs/i18n/**/*.tsx'
);

interface DocsWrapperProps {
  slug: string;
  englishComponent: React.LazyExoticComponent<React.ComponentType>;
}

export default function DocsWrapper({ slug, englishComponent: EnglishComponent }: DocsWrapperProps) {
  const params = useParams();
  const location = useLocation();
  // Determine language from URL path directly. The /:lang/docs/:slug routes
  // are generated as literal paths with no dynamic :lang param, so params.lang
  // is always undefined. Reading i18n.language was unreliable during SSG —
  // singleton state could leak across renders, causing EN URLs to render
  // localized components (observed for managing-projects, subscription-plans).
  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const langFromUrl = firstSegment && LANG_PREFIXES.includes(firstSegment) ? firstSegment : 'en';
  const lang = params.lang || langFromUrl;

  // Use a per-language cloned i18n instance so concurrent SSG renders don't
  // clobber each other's language. We then provide it via I18nextProvider so
  // useTranslation() inside the doc component picks up the right one.
  const langI18n = getI18nForLang(lang);

  const Component = useMemo(() => {
    const effectiveLang = lang;

    if (effectiveLang !== 'en') {
      const filename = DOCS_SLUG_TO_FILE[slug];
      if (filename) {
        const modulePath = `../pages/docs/i18n/${effectiveLang}/${filename}.tsx`;
        const loader = docsI18nModules[modulePath];
        if (loader) {
          return React.lazy(loader);
        }
      }
    }

    return EnglishComponent;
  }, [slug, lang, EnglishComponent]);

  return (
    <I18nextProvider i18n={langI18n}>
      <DocsArticleLayout slug={slug}>
        <Suspense fallback={<div className="min-h-[50vh]" />}>
          <Component />
        </Suspense>
      </DocsArticleLayout>
    </I18nextProvider>
  );
}
